import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  CheckCircleIcon, 
  EnvelopeIcon, 
  ClockIcon, 
  DocumentTextIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const ApplicationSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('applicationId');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container-max py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Application Submitted Successfully!
            </h1>
            <p className="text-lg text-gray-600">
              Thank you for your interest in joining CSEAN
            </p>
          </div>
        </div>
      </div>

      {/* Success Content */}
      <div className="section-padding">
        <div className="container-max max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Success Message */}
            <div className="card">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <CheckCircleIcon className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Your Application is Being Processed
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  We have successfully received your membership application. Our team will review 
                  your application and get back to you within 5-7 business days.
                </p>
              </div>

              {applicationId && (
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Application Details</h3>
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Application ID</p>
                      <p className="font-mono font-semibold text-gray-900">{applicationId}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-start">
                  <EnvelopeIcon className="w-6 h-6 text-primary-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email Confirmation</h3>
                    <p className="text-sm text-gray-600">
                      You will receive a confirmation email shortly with your application details 
                      and next steps.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <ClockIcon className="w-6 h-6 text-primary-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Review Process</h3>
                    <p className="text-sm text-gray-600">
                      Your application will be reviewed by our membership committee within 
                      5-7 business days. You'll be notified of the decision via email.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="space-y-6">
              {/* Check Status Card */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Track Your Application</h3>
                <p className="text-gray-600 mb-4">
                  You can check the status of your application anytime using your application ID.
                </p>
                <Link
                  to="/membership/application-status"
                  className="btn-primary w-full flex items-center justify-center"
                >
                  Check Application Status
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Link>
              </div>

              {/* What's Next Card */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">What Happens Next?</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                      1
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Email Confirmation</p>
                      <p className="text-sm text-gray-600">You'll receive a confirmation email with your application ID</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                      2
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Committee Review</p>
                      <p className="text-sm text-gray-600">Our membership committee will review your application</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                      3
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Decision Notification</p>
                      <p className="text-sm text-gray-600">You'll receive an email with the committee's decision</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                      4
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Membership Activation</p>
                      <p className="text-sm text-gray-600">If approved, you'll receive login credentials for the member portal</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Support */}
              <div className="card bg-primary-50 border-primary-200">
                <h3 className="text-lg font-semibold text-primary-900 mb-2">Need Help?</h3>
                <p className="text-primary-700 mb-4">
                  If you have any questions about your application or need assistance, 
                  please don't hesitate to contact us.
                </p>
                <div className="space-y-2">
                  <a
                    href="mailto:info@csean.org.ng"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <EnvelopeIcon className="w-4 h-4 mr-2" />
                    info@csean.org.ng
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-12 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/membership/categories"
                className="btn-secondary inline-flex items-center justify-center"
              >
                View Membership Categories
              </Link>
              <Link
                to="/"
                className="btn-primary inline-flex items-center justify-center"
              >
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationSuccessPage;
